/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The new-transfer wizard's Readiness step, right after the target is chosen: Swift checks the
 * selected VMs against the target (read-only), estimates the syncs and recommends a size. Next
 * is allowed when every VM is ready or ready with warnings; a blocked VM must be removed, or the
 * user ticks "I understand". A check's fix (fix.option / fix.value) can be applied to the
 * wizard's options with one click; the report is then run again.
 */

import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { PREPARABLE_TARGETS, defaultGuestAccess, windowsGuests } from "@src/components/modules/WizardModule/WizardGuestAccess";
import { T9 } from "@src/components/ui/Lists/ListTable";
import { CheckRow, Notice } from "@src/components/ui/OpsDialog";
import { OptionsSchemaPlugin } from "@src/plugins";
import cutoverSource, { apiErrorMessage } from "@src/sources/CutoverSource";
import guestAccessSource, { instanceKey } from "@src/sources/GuestAccessSource";
import notificationStore from "@src/stores/NotificationStore";
import providerStore from "@src/stores/ProviderStore";
import wizardStore from "@src/stores/WizardStore";

import type { ReadinessCheck, ReadinessInstance, ReadinessRequest } from "@src/@types/Cutover";
import type { WizardData, WizardReadinessState } from "@src/@types/WizardData";

import ReadinessReportView, { fixKey, withoutInstances } from "./ReadinessReport";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
`;
const Intro = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${T9.slate600};
`;

export const defaultReadiness = (): WizardReadinessState => ({
  report: null,
  signature: null,
  override: false,
  skipped: false,
  linkMbps: "",
  error: null,
  fixes: {},
});

/** The readiness body for the wizard's current choices (mirrors what WizardSource sends on create). */
export const buildReadinessRequest = (
  data: WizardData,
  opts: { linkMbps?: string; refresh?: boolean } = {},
): ReadinessRequest | null => {
  if (!data.source || !data.target || !data.selectedInstances?.length) return null;
  const sourceParser = OptionsSchemaPlugin.for(data.source.type);
  const destParser = OptionsSchemaPlugin.for(data.target.type);
  const sourceOptions: any = { ...sourceParser.getDestinationEnv(data.sourceOptions || {}) };
  const targetOptions: any = { ...destParser.getDestinationEnv(data.destOptions || {}) };
  // The guest access page's effect on the transfer (see WizardSource.create).
  const access = data.guestAccess;
  if (access) {
    const windows = windowsGuests(data);
    if (windows.length && access.manual) {
      sourceOptions.windows_drivers_preinstalled = true;
      targetOptions.windows_drivers_preinstalled = true;
    } else if (windows.length && PREPARABLE_TARGETS.includes(data.target.type)) {
      sourceOptions.prepare_guest_for = data.target.type;
    }
  }
  if (data.destOptions?.skip_os_morphing) targetOptions.skip_os_morphing = true;
  const body: ReadinessRequest = {
    instances: data.selectedInstances.map(instanceKey),
    target_endpoint_id: data.target.id,
    target_options: targetOptions,
    source_options: sourceOptions,
    timeout_seconds: 45,
  };
  if (data.networks?.length) body.network_map = destParser.getNetworkMap(data.networks) as any;
  const link = Number(opts.linkMbps);
  if (opts.linkMbps && link > 0) body.link_mbps = link;
  if (opts.refresh) body.refresh = true;
  return body;
};

export const readinessSignature = (data: WizardData) => {
  const body = buildReadinessRequest(data);
  return body ? JSON.stringify({ ...body, instances: [...body.instances].sort(), source: data.source?.id }) : "";
};

/** Whether the wizard may leave the Readiness page. */
export const readinessReady = (data: WizardData): boolean => {
  const r = data.readiness;
  if (!r) return false;
  if (r.skipped) return true;
  if (!r.report || r.signature !== readinessSignature(data)) return false;
  return r.report.verdict !== "blocked" || r.override;
};

/** Where a fix's option lives in the wizard: guest access, target options, or source options. */
export const applyFix = (data: WizardData, check: ReadinessCheck, targetFieldNames: string[]): Partial<WizardData> => {
  const fix = check.fix;
  if (!fix?.option) return {};
  const value = fix.value === undefined ? true : fix.value;
  if (fix.option === "windows_drivers_preinstalled") {
    return { guestAccess: { ...(data.guestAccess || defaultGuestAccess()), manual: true } };
  }
  if (fix.option === "skip_os_morphing" || targetFieldNames.includes(fix.option)) {
    return { destOptions: { ...(data.destOptions || {}), [fix.option]: value } };
  }
  return { sourceOptions: { ...(data.sourceOptions || {}), [fix.option]: value } };
};

type Props = {
  wizardData: WizardData;
};
type State = {
  loading: boolean;
  actionBusy?: string | null;
};

@observer
class WizardReadiness extends React.Component<Props, State> {
  state: State = { loading: false };

  unmounted = false;

  runId = 0;

  componentDidMount() {
    const r = this.readiness;
    if (!r.report || r.signature !== readinessSignature(this.props.wizardData)) {
      this.run(false);
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  get readiness(): WizardReadinessState {
    return this.props.wizardData.readiness || defaultReadiness();
  }

  update(changes: Partial<WizardReadinessState>) {
    wizardStore.updateData({ readiness: { ...this.readiness, ...changes } });
  }

  async run(refresh: boolean) {
    const data = wizardStore.data;
    const body = buildReadinessRequest(data, { linkMbps: this.readiness.linkMbps, refresh });
    if (!body || !data.source) return;
    const signature = readinessSignature(data);
    this.runId += 1;
    const id = this.runId;
    this.setState({ loading: true });
    this.update({ error: null });
    try {
      const report = await cutoverSource.readiness(data.source.id, body);
      if (this.unmounted || id !== this.runId) return;
      this.update({ report, signature, error: null, override: report.verdict === "blocked" ? this.readiness.override : false });
    } catch (err: any) {
      if (this.unmounted || id !== this.runId) return;
      this.update({ error: apiErrorMessage(err, "SwiftIQ could not check the VMs."), signature: null });
    } finally {
      if (!this.unmounted && id === this.runId) this.setState({ loading: false });
    }
  }

  handleFix(vm: ReadinessInstance, check: ReadinessCheck) {
    const data = wizardStore.data;
    const targetFields = providerStore.destinationSchema.map(f => f.name);
    wizardStore.updateData(applyFix(data, check, targetFields));
    this.update({ fixes: { ...this.readiness.fixes, [fixKey(vm.instance, check)]: "applied" } });
    this.run(true);
  }

  /** A fix Swift applies on the source platform (e.g. enable Nutanix Guest Tools with VSS), then re-checks. */
  async handleAction(vm: ReadinessInstance, check: ReadinessCheck) {
    const data = wizardStore.data;
    const action = check.fix?.action;
    if (!data.source || !action) return;
    const key = fixKey(vm.instance, check);
    this.setState({ actionBusy: key });
    try {
      const out = await guestAccessSource.fix({ endpointId: data.source.id, instance: vm.instance, fix: action });
      notificationStore.alert(out.detail || "Done", out.opened === false ? "error" : "success");
      if (out.opened !== false) {
        this.update({ fixes: { ...this.readiness.fixes, [key]: "applied" } });
      }
    } catch (err: any) {
      notificationStore.alert(apiErrorMessage(err, "SwiftIQ could not apply the fix."), "error");
    } finally {
      if (!this.unmounted) this.setState({ actionBusy: null });
    }
    this.run(true);
  }

  handleRemove(vm: ReadinessInstance) {
    const instance = (wizardStore.data.selectedInstances || []).find(i => instanceKey(i) === vm.instance || i.id === vm.instance);
    if (!instance) return;
    wizardStore.toggleInstanceSelection(instance);
    const report = this.readiness.report ? withoutInstances(this.readiness.report, [vm.instance]) : null;
    this.update({ report, signature: readinessSignature(wizardStore.data) });
  }

  render() {
    const r = this.readiness;
    const count = this.props.wizardData.selectedInstances?.length || 0;
    const blocked = r.report?.instances.filter(i => i.verdict === "blocked") || [];
    return (
      <Wrapper data-test-id="wizardReadiness">
        <Intro>
          Before anything is created, SwiftIQ checks {count === 1 ? "the selected VM" : `the ${count} selected VMs`} against{" "}
          {this.props.wizardData.target?.name || "the target"}: operating system and drivers, boot firmware, disks, networks
          and quota. It also estimates how long the syncs take and recommends a target size. Nothing is changed on either side.
        </Intro>
        <ReadinessReportView
          report={r.report}
          loading={this.state.loading}
          error={r.error}
          linkMbps={r.linkMbps}
          onLinkMbpsChange={linkMbps => this.update({ linkMbps })}
          onRefresh={() => this.run(true)}
          onFix={(vm, check) => this.handleFix(vm, check)}
          onAction={(vm, check) => this.handleAction(vm, check)}
          actionBusy={this.state.actionBusy || null}
          fixes={r.fixes}
          onRemove={vm => this.handleRemove(vm)}
          networksHint="on the Networks step"
          instanceCount={count}
        />
        {r.report && r.report.verdict === "blocked" ? (
          <Notice tone="warning" title={`${blocked.length} blocked VM${blocked.length === 1 ? "" : "s"}`}>
            <div style={{ marginBottom: 8 }}>
              Remove {blocked.length === 1 ? "it" : "them"} from the transfer, fix the failed checks and refresh, or continue
              knowing that {blocked.length === 1 ? "its" : "their"} migration is likely to fail.
            </div>
            <CheckRow
              checked={r.override}
              onChange={override => this.update({ override })}
              label="I understand: continue with the blocked VMs"
              description="SwiftIQ still creates the transfer; syncing or deploying a blocked VM is likely to fail."
              testId="readiness-override"
            />
          </Notice>
        ) : null}
        {r.error && !r.report ? (
          <CheckRow
            checked={r.skipped}
            onChange={skipped => this.update({ skipped })}
            label="Continue without a readiness report"
            description="For example when the SwiftIQ API does not offer the readiness check yet."
          />
        ) : null}
      </Wrapper>
    );
  }
}

export default WizardReadiness;
